import styled from '@emotion/styled';
import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import { Size } from '../../styling/AppStyles';
import { Certification } from '../steps/StepCertification';
import { Language } from '../steps/StepCertificationLanguage';
import { Method, methodMap } from '../steps/StepCertificationMethod';
import { Location } from '../steps/StepLocation';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinText from '../styled/ExinText';
import TrainerListItem from './TrainerListItem';

export type Trainer = {
  id: string;
  url: string;
  mail?: string;
  address: string;
  distance?: number;
  img: string;
  name: string;
};

const TrainerListCenterContent = styled.div`
  max-width: 680px;
  margin: 0 auto;
  text-align: center;

  @media screen and (max-width: 680px) {
    margin: 0 10px;
  }
`;

const TrainerListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  row-gap: ${Size.medium};
  // -8 to compensate for border width (i.e. total border width of borders adjecent to gutter with three items per row)
  column-gap: calc(${Size.medium} - 8px);
  > * {
    box-sizing: border-box;
    width: calc(33.333% - ${Size.medium} / 2);
    margin: 0 var(--gap) 0 0;
  }

  @media screen and (max-width: 960px) {
    > * {
      width: calc(50% - ${Size.small} / 2);
    }
  }

  @media screen and (max-width: 600px) {
    > * {
      width: 100%;
    }
  }
`;

function transformServerResponse(garbage: unknown): Array<Trainer> {
  return (garbage as any).hits.hits.map((hit: any) => ({
    id: hit._source.meta?.exillence_id?.[0].value,
    url: hit._source.meta?.url?.[0].value,
    mail: hit._source.meta?.email?.[0].value,
    img: hit._source.logo?.[0],
    // Format address in the same way that Wordpress would do;
    address: [
      `${hit._source.location.street_name} ${hit._source.location.street_number}`,
      `${hit._source.location.post_code} ${hit._source.location.city}`,
      `${hit._source.location.country}`,
    ].join('<br />'),
    distance: hit?.sort?.[0] || undefined,
    name: hit._source.post_title,
  }));
}

const TrainerList: FunctionComponent<{
  certification: Certification;
  language: Language;
  method: Method.ELEARNING_EXAM | Method.TRAINING_EXAM | Method.VIRTUAL_CLASSROOM;
  location: Location | null;
}> = ({ certification, language, location, method }) => {
  const sort: Array<any> = [];
  if (location) {
    sort.push({
      _geo_distance: {
        'location.coordinates': {
          lat: location.lat,
          lon: location.lng,
        },
        order: 'asc',
        unit: 'km',
        mode: 'min',
        distance_type: 'arc',
      },
    });
  }
  const { isLoading, response: trainers } = useElasticSearch<Array<Trainer>>(
    {
      query: {
        bool: {
          must: [
            {
              term: {
                'post_type.raw': 'ex_partner',
              },
            },
            {
              nested: {
                path: 'accreditations',
                query: {
                  bool: {
                    must: [
                      {
                        term: {
                          'accreditations.certification_id': certification.value,
                        },
                      },
                      {
                        term: {
                          'accreditations.training_language_name': language.value,
                        },
                      },
                      {
                        term: {
                          'accreditations.training_method_id': methodMap[method],
                        },
                      },
                    ],
                  },
                },
              },
            },
          ],
          filter: location
            ? {
                // Search ElasticSearch entries within a certain range from coordinates.
                // More info:
                //   https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-geo-distance-query.html
                geo_distance: {
                  distance: location.range < 250 ? `${location.range}km` : '40000km',
                  'location.coordinates': {
                    lat: location.lat,
                    lon: location.lng,
                  },
                },
              }
            : null,
        },
      },
      size: 10000,
      _source: [
        'meta.exillence_id.value',
        'meta.url.value',
        'meta.email.value',
        'location.street_name',
        'location.street_number',
        'location.post_code',
        'location.city',
        'location.country',
        'post_title',
        'logo',
      ],
      sort,
    },
    transformServerResponse
  );

  if (isLoading) {
    return (
      <TrainerListCenterContent>
        <ExinMuiCircularProgress />
      </TrainerListCenterContent>
    );
  }

  if (!trainers?.length) {
    return <Trans i18nKey="messageNoTrainers">No trainers found</Trans>;
  }

  const count = trainers.length;
  return (
    <>
      <TrainerListCenterContent>
        <ExinText>{count} result</ExinText>
      </TrainerListCenterContent>
      <TrainerListContainer>
        {trainers.map((trainer) => (
          <TrainerListItem key={trainer.id} trainer={trainer} />
        ))}
      </TrainerListContainer>
    </>
  );
};

export default TrainerList;
